var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import * as pc from "playcanvas";
var collectMaterials = function (entity, materials) {
    var e_1, _a;
    var _b, _c;
    /* A model component with no asset loaded yet (the central pump's model
     * entity while the node is a placeholder) has no mesh instances at all. */
    (_c = (_b = entity.model) === null || _b === void 0 ? void 0 : _b.meshInstances) === null || _c === void 0 ? void 0 : _c.map(function (mesh) { return mesh.material; }).forEach(function (material) { return materials.add(material); });
    try {
        for (var _d = __values(entity.children), _e = _d.next(); !_e.done; _e = _d.next()) {
            var child = _e.value;
            collectMaterials(child, materials);
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_e && !_e.done && (_a = _d.return)) _a.call(_d);
        }
        finally { if (e_1) throw e_1.error; }
    }
};
var recursivelySetMaterial = function (entity, material) {
    var e_2, _a;
    var _b;
    var meshes = (_b = entity.model) === null || _b === void 0 ? void 0 : _b.meshInstances;
    if (meshes && meshes.length > 0) {
        meshes.forEach(function (meshInstance) {
            meshInstance.material = material(meshInstance);
        });
    }
    try {
        for (var _c = __values(entity.children), _d = _c.next(); !_d.done; _d = _c.next()) {
            var child = _d.value;
            recursivelySetMaterial(child, material);
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
        }
        finally { if (e_2) throw e_2.error; }
    }
};
/*
 * The material a mesh instance had before `transformEntityMaterials` swapped
 * it for a faded/emphasized clone, so `resetMaterial` can put it back for
 * materials that are not PlayCanvas assets (the central pump placeholder's
 * plain grey, say). Asset materials are reset from `materialAssets` by name
 * as before. Weak so destroyed mesh instances are not kept alive.
 */
var originalMaterials = new WeakMap();
var transformEntityMaterials = function (entity, transformFn) {
    var materialSet = new Set();
    collectMaterials(entity, materialSet);
    var materials = Array.from(materialSet);
    var transformedMaterials = materials.map(transformFn);
    var material = function (meshInstance) {
        if (!originalMaterials.has(meshInstance)) {
            originalMaterials.set(meshInstance, meshInstance.material);
        }
        var index = materials.findIndex(function (material) { return material === meshInstance.material; });
        return transformedMaterials[index];
    };
    recursivelySetMaterial(entity, material);
};
/*
 * Materials with an emphasized look of their own, used instead of the darkened
 * clone: the placeholder icons (`icon-billboard`) light up as a whole icon in
 * their hover colours rather than going dark.
 */
var emphasizedVariants = new WeakMap();
export var setEmphasizedVariant = function (material, emphasized) {
    emphasizedVariants.set(material, emphasized);
};
var emphasizeMaterial = function (material) {
    var variant = emphasizedVariants.get(material);
    if (variant)
        return variant;
    var emphasized = material.clone();
    emphasized.diffuse.r *= 0.5;
    emphasized.diffuse.g *= 0.5;
    emphasized.diffuse.b *= 0.5;
    emphasized.update();
    return emphasized;
};
var fadeMaterial = function (material) {
    var faded = material.clone();
    faded.opacity = 0.2 * faded.opacity;
    faded.blendType = pc.BLEND_NORMAL;
    faded.update();
    return faded;
};
/*
 * Lighten a material towards white. Used to mark a selected cardboard box:
 * the box is already a mid-brown, so darkening it (see `emphasizeMaterial`)
 * reads as a shadow rather than as "this one is selected".
 */
var highlightMaterial = function (material) {
    var highlighted = material.clone();
    var lighten = function (value) { return value + (1 - value) * 0.45; };
    highlighted.diffuse.r = lighten(highlighted.diffuse.r);
    highlighted.diffuse.g = lighten(highlighted.diffuse.g);
    highlighted.diffuse.b = lighten(highlighted.diffuse.b);
    highlighted.update();
    return highlighted;
};
/*
 * Blend a material's diffuse towards a tint colour. Used to mark the cardboard
 * boxes the cups actually lift: the box keeps its cardboard shading and
 * texture, but reads as green/red at a glance so it is obvious which boxes
 * carry lifting force when a pallet is full of them.
 */
var tintMaterial = function (tint, strength) { return function (material) {
    var tinted = material.clone();
    var blend = function (value, target) {
        return value + (target - value) * strength;
    };
    tinted.diffuse.r = blend(tinted.diffuse.r, tint.r);
    tinted.diffuse.g = blend(tinted.diffuse.g, tint.g);
    tinted.diffuse.b = blend(tinted.diffuse.b, tint.b);
    tinted.update();
    return tinted;
}; };
/*
 * Make a material a flat light grey, texture and all. Used for the boxes of a
 * stacking layer that is not being worked on, so they read as background
 * rather than as boxes that can be picked.
 */
var greyOutMaterial = function (material) {
    var greyed = material.clone();
    greyed.diffuseMap = null;
    greyed.diffuse = new pc.Color(0.5, 0.5, 0.5);
    greyed.update();
    return greyed;
};
export var greyOutEntity = function (entity) {
    transformEntityMaterials(entity, greyOutMaterial);
};
export var emphasizeEntity = function (entity) {
    transformEntityMaterials(entity, emphasizeMaterial);
};
export var highlightEntity = function (entity) {
    transformEntityMaterials(entity, highlightMaterial);
};
export var tintEntity = function (entity, tint, strength) {
    if (strength === void 0) { strength = 0.5; }
    transformEntityMaterials(entity, tintMaterial(tint, strength));
};
export var fadeEntity = function (entity) {
    transformEntityMaterials(entity, fadeMaterial);
};
export var resetMaterial = function (entity, materialAssets) {
    var e_3, _a;
    if (entity.model && entity.model.meshInstances) {
        entity.model.meshInstances.forEach(function (meshInstance) {
            var asset = materialAssets[meshInstance.material.name];
            var original = asset
                ? asset.resource
                : originalMaterials.get(meshInstance);
            if (original)
                meshInstance.material = original;
        });
    }
    try {
        for (var _b = __values(entity.children), _c = _b.next(); !_c.done; _c = _b.next()) {
            var child = _c.value;
            resetMaterial(child, materialAssets);
        }
    }
    catch (e_3_1) { e_3 = { error: e_3_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_3) throw e_3.error; }
    }
};
export var getMaterialAssets = function (materialAssets, materialTransforms) {
    if (!materialTransforms)
        return;
    return materialTransforms.reduce(function (acc, transform) {
        acc[transform.sourceMaterial] = materialAssets[transform.targetMaterial];
        return acc;
    }, {});
};
export var getMaterialTransformsForObjectShape = function (objectShape, useTexturedSurfaces) {
    var objectHasTexturedMaterials = objectShape.texturedMaterialTransforms !== undefined;
    if (objectHasTexturedMaterials && useTexturedSurfaces) {
        return objectShape.texturedMaterialTransforms;
    }
    return objectShape.materialTransforms;
};
export var applyMaterialTransforms = function (entity, transforms, uvScale) {
    if (!entity.model || !transforms)
        return;
    entity.model.meshInstances.forEach(function (meshInstance) {
        var materialTransformAsset = transforms === null || transforms === void 0 ? void 0 : transforms[meshInstance.material.name];
        if (materialTransformAsset) {
            var material = materialTransformAsset.resource.clone();
            if (uvScale) {
                material.diffuseMapTiling = uvScale;
                material.glossMapTiling = uvScale;
                material.normalMapTiling = uvScale;
                material.update();
            }
            meshInstance.material = material;
        }
    });
};
